import { useRef, useState } from "react";
import { getUsersApi, updateTenantSettings } from "@praesto/core";
import { Badge, Card, FormField, Input, Section, Tag } from "@praesto/ui-kit";
import { useCore } from "../lib/core";
import { IntegrationsSettings } from "./IntegrationsSettings";
import { NotificationRulesSettings } from "./NotificationRulesSettings";

/** Customer-facing module names (root CLAUDE.md): code ids never reach the UI. */
const MODULE_LABEL: Record<string, string> = {
  procedure: "Procedure",
  shifts: "Turni",
  monitoring: "Monitoraggio",
};

/**
 * Tenant settings — owner only. Edits the per-tenant session duration
 * (mobile / desktop JWT lifetimes) and shows the active modules read-only.
 * The route gates this panel on owner; the server endpoint is owner-gated too.
 */
export function TenantSettings() {
  const core = useCore();
  const t = core.i18n.t;
  const settings = (() => {
    try {
      return core.tenant.settings();
    } catch {
      return null;
    }
  })();

  const [mobile, setMobile] = useState(settings?.sessionDuration.mobile ?? "P30D");
  const [desktop, setDesktop] = useState(settings?.sessionDuration.desktop ?? "PT12H");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function debouncedSave(next: { mobile: string; desktop: string }) {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void (async () => {
        setStatus("saving");
        try {
          const api = getUsersApi();
          const tok = await api.getAuthToken();
          await updateTenantSettings(
            api.apiBase,
            { sessionDuration: { mobile: next.mobile, desktop: next.desktop } },
            tok,
          );
          setStatus("saved");
          window.setTimeout(() => setStatus("idle"), 1500);
        } catch {
          setStatus("error");
        }
      })();
    }, 600);
  }

  const activeModules = core.tenant.activeModules();

  return (
    <div className="space-y-6">
      <Section width="readable">
        <div className="h-6 flex items-center gap-2">
          <Badge tone="warning" variant="outline" size="xs">
            {t("settings.tenant.ownerOnly")}
          </Badge>
          {status === "saving" && (
            <Badge tone="neutral" variant="soft" size="xs">
              {t("settings.saving")}
            </Badge>
          )}
          {status === "saved" && (
            <Badge tone="success" variant="soft" size="xs">
              {t("settings.saved")}
            </Badge>
          )}
          {status === "error" && (
            <Badge tone="danger" variant="soft" size="xs">
              {t("settings.error")}
            </Badge>
          )}
        </div>
      </Section>

      <Section width="readable">
        <Card>
          <div className="mb-2 text-eyebrow">{t("settings.tenant.sessionDuration")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={t("settings.tenant.sessionDuration.mobile")}>
              <Input
                value={mobile}
                placeholder="P30D"
                onChange={(e) => {
                  setMobile(e.target.value);
                  debouncedSave({ mobile: e.target.value, desktop });
                }}
              />
            </FormField>
            <FormField label={t("settings.tenant.sessionDuration.desktop")}>
              <Input
                value={desktop}
                placeholder="PT12H"
                onChange={(e) => {
                  setDesktop(e.target.value);
                  debouncedSave({ mobile, desktop: e.target.value });
                }}
              />
            </FormField>
          </div>
          <div className="mt-2 text-caption text-fg-muted">
            {t("settings.tenant.sessionDuration.hint")}
          </div>
        </Card>
      </Section>

      <Section width="readable">
        <Card>
          <div className="mb-2 text-eyebrow">{t("settings.tenant.modules")}</div>
          <div className="flex flex-wrap gap-1.5">
            {/* Read-only metadata, not a tappable filter: Tag, not Chip. */}
            {activeModules.map((m) => (
              <Tag key={m}>{MODULE_LABEL[m] ?? m}</Tag>
            ))}
          </div>
          <div className="mt-2 text-caption text-fg-muted">
            {t("settings.tenant.modules.readonly")}
          </div>
        </Card>
      </Section>

      <Section>
        <NotificationRulesSettings />
      </Section>

      {/* Readable, not full: connector cards are form-shaped — labelled fields
          and a mapping table read better capped than stretched edge to edge. */}
      <Section width="readable">
        <IntegrationsSettings />
      </Section>
    </div>
  );
}
